import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from 'components/Assessments/adapters/types'
import { SectionAdapter } from 'components/Assessments/adapters/types'
import { CertPeriodRecapFormField } from './formField'
import { CertPeriodRecapQuestionnaireItem } from './questionnaire'
import { CertPeriodRecapGoalsInfo } from './components/RecapGoalsSubsection'
import { RecertFormData } from '../../types/RecertFormData'
import { createAnswerItemsFromValues, getAnswerValuesFromItems } from '../../../helpers'

export class CertPeriodRecapAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract consent reference
    const [consentRef] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CONSENT
    )

    // Extract medical history boolean values
    const [erHistory] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_ER
    )
    const [hospitalHistory] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_HOSPITAL
    )
    const [injuryHistory] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INJURIES
    )
    const [fallHistory] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_FALLS
    )
    const [infectionHistory] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INFECTIONS
    )
    const [mdHistory] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_MD
    )

    // Extract notes
    const [erNotes] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_ER_NOTES
    )
    const [hospitalNotes] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_HOSPITAL_NOTES
    )
    const [injuryNotes] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INJURIES_NOTES
    )
    const [fallNotes] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_FALLS_NOTES
    )
    const [infectionNotes] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INFECTIONS_NOTES
    )
    const [mdNotes] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_MD_NOTES
    )

    // Extract goals array
    const goalsAnswers =
      flatAnswers[CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_GOALS]?.answer
    const goals: CertPeriodRecapGoalsInfo[] =
      goalsAnswers?.map((goalAnswer) => {
        const goalItems = goalAnswer.item ?? []
        const [goalId] = getAnswerValuesFromItems(
          goalItems,
          CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_GOALS_GOAL_ID
        )
        const [status] = getAnswerValuesFromItems(
          goalItems,
          CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_GOALS_GOAL_STATUS
        )

        return {
          id: goalId,
          status
        }
      }) ?? []

    // Extract initials authorization
    const [initials] = getAnswerValuesFromItems(
      flatAnswersArray,
      CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_INITIALS_AUTHORIZATION
    )

    return {
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_CONSENT]: consentRef,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_ER]: erHistory,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_ER_NOTES]: erNotes,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_HOSPITAL]: hospitalHistory,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_HOSPITAL_NOTES]: hospitalNotes,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INJURIES]: injuryHistory,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INJURIES_NOTES]: injuryNotes,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_FALLS]: fallHistory,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_FALLS_NOTES]: fallNotes,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INFECTIONS]: infectionHistory,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INFECTIONS_NOTES]: infectionNotes,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_MD]: mdHistory,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_MD_NOTES]: mdNotes,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_GOALS]: goals,
      [CertPeriodRecapFormField.CERT_PERIOD_RECAP_INITIALS_AUTHORIZATION]: initials
    }
  }

  buildFlatAnswers(formData: RecertFormData): Array<{ linkId: string; value: unknown }> {
    const mutations: Array<{ linkId: string; value: unknown }> = []
    // Build medical history boolean mutations
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_ER] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_ER,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_ER]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_HOSPITAL] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_HOSPITAL,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_HOSPITAL]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INJURIES] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INJURIES,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INJURIES]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_FALLS] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_FALLS,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_FALLS]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INFECTIONS] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INFECTIONS,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INFECTIONS]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_MD] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_MD,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_MD]
      })
    }

    // Build notes mutations
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_ER_NOTES] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_ER_NOTES,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_ER_NOTES]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_HOSPITAL_NOTES] !== undefined) {
      mutations.push({
        linkId:
          CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_HOSPITAL_NOTES,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_HOSPITAL_NOTES]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INJURIES_NOTES] !== undefined) {
      mutations.push({
        linkId:
          CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INJURIES_NOTES,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INJURIES_NOTES]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_FALLS_NOTES] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_FALLS_NOTES,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_FALLS_NOTES]
      })
    }
    if (
      formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INFECTIONS_NOTES] !== undefined
    ) {
      mutations.push({
        linkId:
          CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_INFECTIONS_NOTES,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_INFECTIONS_NOTES]
      })
    }
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_MD_NOTES] !== undefined) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_CARE_STATUS_60_DAY_MD_NOTES,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_60_DAY_MD_NOTES]
      })
    }

    // Build goals array mutations
    const goals = formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_GOALS]
    if (goals && goals.length > 0) {
      const goalsAnswerItems = goals.map((goal) => {
        const goalAnswerItems = createAnswerItemsFromValues([
          [CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_GOALS_GOAL_ID, goal.id],
          [CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_GOALS_GOAL_STATUS, goal.status]
        ])

        return {
          item: goalAnswerItems
        }
      })

      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_GOALS,
        value: goalsAnswerItems
      })
    }

    // Build initials authorization mutation
    if (formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_INITIALS_AUTHORIZATION]) {
      mutations.push({
        linkId: CertPeriodRecapQuestionnaireItem.CERT_PERIOD_RECAP_INITIALS_AUTHORIZATION,
        value: formData[CertPeriodRecapFormField.CERT_PERIOD_RECAP_INITIALS_AUTHORIZATION]
      })
    }

    return mutations
  }
}
